import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {getUserId} from '@Libraries/user.library';
import type {GridUserImageItemStorage} from '@Components/base-grid/components/grid-user-image-item';
import {getGridUserImageItemStorageForUserImageVO} from '@Components/base-grid/components/grid-user-image-item';
import {openErrorModal} from '@Modals/error-modal';
import type {UserImageVOResponse} from '@Libraries/user-image-library';
import {checkUploadStatus, UserMediaType} from '@Libraries/user-media-library';
import {imageUrlToDataUrl} from '@Utils/image.util';
import {v4 as uuidv4} from 'uuid';
import type {TempImageData} from '@Libraries/add-media-library';
import {ElementDataType} from '@Libraries/add-media-library';
import {ImageItemSource} from '@Libraries/image-item.library';
import type {DropboxChooserFile} from '@/types/dropbox';

let onAddSelected: (item: Array<GridUserImageItemStorage | TempImageData>) => void;
let onStartAdd: () => void;
let shouldLoadDataUrlImage = false;

let apiLoaded = false;

export const openDropboxChooser = async (
  onAddItem: (item: Array<GridUserImageItemStorage | TempImageData>) => void,
  onStartAddSelected: () => void,
  allowLoadDataUrlImage = false
): Promise<void> => {
  onAddSelected = onAddItem;
  onStartAdd = onStartAddSelected;
  shouldLoadDataUrlImage = allowLoadDataUrlImage;
  showLoading('loadingDropbox');
  try {
    const apikey = (await window.PMW.readLocal('dropbox/getAppKey')) as string;
    await loadDropboxApi(apikey);
    loadDropboxChooser();
  } catch (e) {
    console.error(e);
    openErrorModal({
      message: window.i18next.t('pmwjs_general_ajax_error'),
    });
  } finally {
    hideLoading('loadingDropbox');
  }
};

const loadDropboxApi = (apikey: string): Promise<void> | void => {
  if (apiLoaded) {
    return;
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.setAttribute('type', 'text/javascript');
    script.setAttribute('id', 'dropboxjs');
    script.setAttribute('data-app-key', apikey);
    script.onload = (): void => {
      apiLoaded = true;
      resolve();
    };
    script.onerror = reject;
    script.setAttribute('src', 'https://www.dropbox.com/static/api/2/dropins.js');
    document.getElementsByTagName('head')[0].appendChild(script);
  });
};

const loadDropboxChooser = (): void => {
  window.Dropbox?.choose({
    success: (files: readonly DropboxChooserFile[]): void => {
      void chooserCallback(files);
    },
    linkType: 'direct',
    multiselect: true,
    extensions: ['.jpg', '.jpeg', '.png', '.gif'],
  });
};

const chooserCallback = async (files: readonly DropboxChooserFile[]): Promise<void> => {
  let error = false;

  showLoading('uploadingDropboxItems');
  onStartAdd();

  for (let i = 0; i < files.length; i++) {
    try {
      let data;
      if (shouldLoadDataUrlImage && files[i].link) {
        // eslint-disable-next-line no-await-in-loop
        const dataurl = await imageUrlToDataUrl(files[i].link);
        if (dataurl) {
          const tempUploadingImageUID = uuidv4();

          data = {
            uid: tempUploadingImageUID,
            dataUrl: dataurl,
            type: ElementDataType.IMAGE,
            source: ImageItemSource.UPLOAD,
          } as TempImageData;

          void uploadImageItem(files[i], tempUploadingImageUID);
          onAddSelected([data]);
        } else {
          // eslint-disable-next-line no-await-in-loop
          data = await uploadImageItem(files[i]);
          onAddSelected([getGridUserImageItemStorageForUserImageVO(data)]);
        }
      } else {
        // eslint-disable-next-line no-await-in-loop
        data = await uploadImageItem(files[i]);
        onAddSelected([getGridUserImageItemStorageForUserImageVO(data)]);
      }
    } catch (e) {
      console.error(e);
      error = true;
    }
  }

  if (error) {
    openErrorModal({
      message: window.i18next.t('pmwjs_general_ajax_error'),
    });
  }
  hideLoading('uploadingDropboxItems');
};

const uploadImageItem = async (file: DropboxChooserFile, tempUploadingImageUID?: string): Promise<UserImageVOResponse> => {
  return (await window.PMW.pollingAjaxCallAsync(
    () => {
      return window.PMW.writeLocal('posterimage/importDropboxPhoto', {
        userId: getUserId(),
        url: file.link,
        tempUploadingImageUID,
      });
    },
    checkUploadStatus.bind(null, UserMediaType.IMAGE)
  )) as UserImageVOResponse;
};
